import React, { useEffect, useState } from "react";
import { Button, Card, Dropdown, Space, Table, Tabs } from "antd";
import { EllipsisOutlined, LoadingOutlined } from "@ant-design/icons";
import { TimeQuarter } from "../../../types/TimeTypes";
import { Pagination, SortOrder } from "../../../types/DataFetchingTypes";
import { Supplier, SupplierJob } from "../../../types/SupplierTypes";
import {
    deleteSupplier,
    listSuppliers,
    updateSupplier,
    getUserSupplierSetting,
    updateUserSupplierSetting,
    getPendingSupplierJobs,
} from "../../../common/api/SupplierClient";
import { useUserState } from "../../../common/UserContext";
import { handleError } from "../../../common/ErrorHandling";
import { CompanyImageCell } from "../../../common/components/CompanyImageCell";
import { ScoreTag } from "../../../common/components/ScoreTag";
import { sleep } from "../../../common/AsyncUtil";
import { SupplierGenratorPanel } from "./SupplierGeneratorPanel";
import { SupplierEditModal } from "./components/SupplierEditModal";

enum TabKey {
    Existing = "existing",
    Processing = "processing",
}

export const SupplierView = () => {
    const userState = useUserState();
    const [selectedQuarter] = useState<TimeQuarter>();
    const [pagination, setPagination] = useState({
        pages: 0,
        current: 1,
        total: 0,
        pageSize: 10,
    } as Pagination);
    const [data, setData] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(0);
    const [showGenerator, setShowGenerator] = useState(false);
    const [supplierToEdit, setSupplierToEdit] = useState({} as Supplier);
    const [showEditModal, setShowEditModal] = useState(false);

    const [alwaysShowGenerator, setAlwaysShowGenerator] = useState(false);
    const [activeTab, setActiveTab] = useState(TabKey.Existing);
    const [jobs, setJobs] = useState<SupplierJob[]>([]);
    const [jobRefresh, setJobRefresh] = useState(0);

    const handleTableChange = (pagination: any) => {
        setPagination(pagination);

        setReload(Date.now());
    };

    const refreshTable = async () => {
        try {
            setLoading(true);
            const res = await listSuppliers(
                pagination,
                [
                    {
                        attribute: "id",
                        order: SortOrder.DESC,
                    },
                ],
                [],
                selectedQuarter
            );
            setData(res.data);
            setPagination(res.pagination);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const editSupplier = async (form: Supplier) => {
        await updateSupplier(form);
        setShowEditModal(false);
        refreshTable();
    };

    const removeSupplier = async (id: number) => {
        await deleteSupplier(id);
        refreshTable();
    };

    const refreshUserSupplierSetting = async () => {
        if (userState.me?.id) {
            const setting = await getUserSupplierSetting(userState.me.id);
            setAlwaysShowGenerator(setting.alwaysShowGenerator);
            if (!showGenerator) {
                setShowGenerator(setting.alwaysShowGenerator);
            }
        }
    };

    const editUserSupplierSetting = async (checked: boolean) => {
        if (userState.me?.id) {
            await updateUserSupplierSetting({
                userId: userState.me.id,
                alwaysShowGenerator: checked,
            });

            refreshUserSupplierSetting();
        }
    };

    const refreshJobTable = async () => {
        try {
            const res = await getPendingSupplierJobs();
            const newJobIds = res.map((job) => job.id);
            if (jobs.some((job) => !newJobIds.includes(job.id))) {
                setReload(Date.now());
            }
            setJobs(res);
            if (res.length) {
                await sleep(5000);
                setJobRefresh(Date.now());
            }
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        refreshUserSupplierSetting();
    }, [userState]);

    useEffect(() => {
        refreshTable();
    }, [reload]);

    useEffect(() => {
        refreshJobTable();
    }, [jobRefresh]);

    const columns = [
        {
            title: "Company name",
            dataIndex: "name",
            key: "name",
            render: (value: string, row: Supplier) => {
                return (
                    <CompanyImageCell
                        name={value}
                        logo={row.logo}
                        link={`/supplier/${row.id}`}
                        domain={row.website}
                        linkedin={row.linkedIn}
                        twitter={row.twitter}
                    />
                );
            },
        },
        {
            title: "ESG score",
            dataIndex: "esgScore",
            key: "esgScore",
            render: (value: number) => {
                return <ScoreTag score={value} type="fraction" />;
            },
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "Actions",
            dataIndex: "id",
            key: "actions",
            render: (value: number, row: Supplier) => {
                return (
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    label: (
                                        <Button
                                            type="text"
                                            onClick={() => {
                                                setSupplierToEdit(row);
                                                setShowEditModal(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    ),
                                    key: "edit",
                                },
                                {
                                    label: (
                                        <Button
                                            type="text"
                                            danger
                                            onClick={() => removeSupplier(value)}
                                        >
                                            Delete
                                        </Button>
                                    ),
                                    key: "delete",
                                },
                            ],
                        }}
                    >
                        <EllipsisOutlined style={{ color: "#1890FF" }} />
                    </Dropdown>
                );
            },
        },
    ];

    const supplierTable = (
        <Table
            dataSource={data}
            pagination={pagination}
            loading={loading}
            rowKey="id"
            onChange={handleTableChange}
            columns={columns}
        ></Table>
    );

    const jobColumns = [
        {
            title: "Company name",
            key: "name",
            render: (_: any, row: SupplierJob) => {
                return <CompanyImageCell name={row.baseInfo.name} />;
            },
        },
        {
            title: "",
            dataIndex: "",
            key: "description",
            render: (_: any, row: SupplierJob) => {
                return (
                    <span>
                        Generating buyer brief for <strong>{row.baseInfo.name}</strong>...
                        <LoadingOutlined />
                    </span>
                );
            },
        },
    ];
    const jobTable = (
        <Table dataSource={jobs} pagination={false} rowKey="id" columns={jobColumns}></Table>
    );

    const panel = (
        <div>
            <Card bordered={false}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 20,
                    }}
                >
                    <div>
                        <div style={{ fontSize: 16 }}>Buyer briefs</div>
                        <div style={{ color: "#666" }}>
                            Unlock detailed insights into prospective 3rd party business partners
                            including in-depth company analysis, competitor overview,
                            differentiators, and key negotiation strategies to secure the best deal
                            possible.
                        </div>
                    </div>
                    <Space>
                        <Button type="primary" onClick={() => setShowGenerator(true)}>
                            Add Buyer brief
                        </Button>
                        <SupplierGenratorPanel
                            open={showGenerator}
                            onClose={() => setShowGenerator(false)}
                            onCreated={() => {
                                setJobRefresh(Date.now());
                                setShowGenerator(false);
                                setActiveTab(TabKey.Processing);
                            }}
                            alwaysShow={alwaysShowGenerator}
                            onAlwaysShowChange={editUserSupplierSetting}
                        />
                    </Space>
                </div>
                <Tabs
                    activeKey={activeTab}
                    items={[
                        { label: "Existing", key: TabKey.Existing, children: supplierTable },
                        { label: "Processing", key: TabKey.Processing, children: jobTable },
                    ]}
                    onChange={(key) => setActiveTab(key as TabKey)}
                ></Tabs>
            </Card>
            <SupplierEditModal
                open={Boolean(showEditModal && supplierToEdit.id)}
                supplier={supplierToEdit}
                onCancel={() => setShowEditModal(false)}
                onOk={(form) => {
                    editSupplier(form);
                }}
            />
        </div>
    );

    return panel;
};
